import type React from "react";
import { useMemo } from "react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ChevronLeft, ChevronRight, Undo2, Redo2, ZoomIn, ZoomOut } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { format, addMonths, startOfMonth } from "date-fns";
import type { ZoomLevel } from "@/hooks/useTankPlanningState";
import type { PlannerViewer } from "@/hooks/usePlannerPresence";
import { PlannerPresenceAvatars } from "./PlannerPresenceAvatars";

interface TimelineControlsProps {
  currentDate: Date;
  zoomLevel: ZoomLevel;
  vesselTypeFilter: string;
  facilityFilter: string;
  onNavigate: (direction: 'prev' | 'next' | 'today') => void;
  onZoomChange: (level: ZoomLevel) => void;
  onFilterChange: (filter: string) => void;
  onFacilityFilterChange: (filter: string) => void;
  onGoToDate: (date: Date) => void;
  vesselTypes: { value: string; label: string }[];
  facilities: { value: string; label: string }[];
  canUndo?: boolean;
  canRedo?: boolean;
  undoLabel?: string;
  redoLabel?: string;
  onUndo?: () => void;
  onRedo?: () => void;
  presenceSelf?: PlannerViewer | null;
  viewers?: PlannerViewer[];
  /** Extra controls rendered before the facility filter (customer portal: My tanks / brand). */
  extraControls?: React.ReactNode;
  /** Customer portal: hide undo/redo and presence avatars. */
  hideHistory?: boolean;
  hidePresence?: boolean;
}

export function TimelineControls({
  currentDate,
  zoomLevel,
  vesselTypeFilter,
  facilityFilter,
  onNavigate,
  onZoomChange,
  onFilterChange,
  onFacilityFilterChange,
  onGoToDate,
  vesselTypes,
  facilities,
  canUndo = false,
  canRedo = false,
  undoLabel,
  redoLabel,
  onUndo,
  onRedo,
  presenceSelf = null,
  viewers = [],
  extraControls,
  hideHistory = false,
  hidePresence = false,
}: TimelineControlsProps) {
  // Generate month options: 24 months back, current, 24 months forward
  const monthOptions = useMemo(() => {
    const options: { value: string; label: string }[] = [];
    const now = new Date();
    for (let i = -24; i <= 24; i++) {
      const month = startOfMonth(addMonths(now, i));
      options.push({
        value: format(month, "yyyy-MM"), // Use simple format to avoid timezone issues
        label: format(month, "MMMM yyyy"),
      });
    }
    return options;
  }, []);

  const currentMonthValue = format(startOfMonth(currentDate), "yyyy-MM");

  const handleMonthChange = (value: string) => {
    // Parse yyyy-MM format
    const [year, month] = value.split('-').map(Number);
    onGoToDate(new Date(year, month - 1, 1));
  };

  return (
    <TooltipProvider>
    <div className="flex items-center justify-between gap-4 p-4 border-b bg-card">
      {/* Left: Navigation + Undo/Redo */}
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="icon"
          onClick={() => onNavigate('prev')}
          title="Previous"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>

        <Button
          variant="outline"
          size="sm"
          onClick={() => onNavigate('today')}
          className="px-3"
        >
          Today
        </Button>

        <Button
          variant="outline"
          size="icon"
          onClick={() => onNavigate('next')}
          title="Next"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>

        {/* Month Picker Dropdown */}
        <Select value={currentMonthValue} onValueChange={handleMonthChange}>
          <SelectTrigger className="w-44 ml-2">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {monthOptions.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Undo / Redo */}
        {!hideHistory && (
        <div className="flex items-center gap-1 ml-2">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className={cn("h-8 w-8", !canUndo && "opacity-40")}
                disabled={!canUndo}
                onClick={onUndo}
              >
                <Undo2 className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{undoLabel ? `Undo: ${undoLabel}` : 'Nothing to undo'}</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className={cn("h-8 w-8", !canRedo && "opacity-40")}
                disabled={!canRedo}
                onClick={onRedo}
              >
                <Redo2 className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{redoLabel ? `Redo: ${redoLabel}` : 'Nothing to redo'}</TooltipContent>
          </Tooltip>
        </div>
        )}
      </div>

      {/* Center: Presence avatars */}
      {!hidePresence && <PlannerPresenceAvatars self={presenceSelf} viewers={viewers} />}

      {/* Right: Zoom and Filters */}
      <div className="flex items-center gap-3">
        {extraControls}

        {/* Facility Filter */}
        <Select value={facilityFilter} onValueChange={onFacilityFilterChange}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="All Facilities" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Facilities</SelectItem>
            {facilities.map((f) => (
              <SelectItem key={f.value} value={f.value}>
                {f.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Vessel Type Filter */}
        <Select value={vesselTypeFilter} onValueChange={onFilterChange}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="All Types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {vesselTypes.map((type) => (
              <SelectItem key={type.value} value={type.value}>
                {type.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Zoom Controls */}
        <div className="flex items-center gap-1 border rounded-md p-1 bg-muted/30">
          <Button
            variant={zoomLevel === 'day' ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => onZoomChange('day')}
            className="px-3 transition-all duration-200"
          >
            <ZoomIn className="h-3 w-3 mr-1" />
            Day
          </Button>
          <Button
            variant={zoomLevel === 'week' ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => onZoomChange('week')}
            className="px-3 transition-all duration-200"
          >
            Week
          </Button>
          <Button
            variant={zoomLevel === 'month' ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => onZoomChange('month')}
            className="px-3 transition-all duration-200"
          >
            <ZoomOut className="h-3 w-3 mr-1" />
            Month
          </Button>
        </div>
      </div>
    </div>
    </TooltipProvider>
  );
}
